import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Box, CircularProgress, Typography } from '@mui/material';
import { BackButtonHeader } from '../ui-components/back-button-header.component';
import {
  ZendeskChat,
  renderZendeskChat,
  ConversationField,
  ZENDESK_FIELD_IDS,
  useZendeskChatReady,
} from './zendesk-chat.component';
import { useAuth } from '../../hooks/useAuth';
import { useFetchChargerDetailQuery } from '../../services/chargers.api';
import { useFetchListOfPayMethodsQuery } from '../../services/payment.api';
import { useFetchActiveSessionsQuery } from '../../services/sessions.api';
import { usePostHogFeatureFlag } from '../../hooks/usePostHogFeatureFlag';
import { useZendeskChatConfig } from '../../services/useZendeskChatConfig';
import { ROUTES } from '../../types/routes.enum';
import { manageAccountIcon } from '../../assets/icons/icons';
import { ViewWrapper } from '../ui-components/view-wrapper.component';
import { PricingType } from '../../stores/types/price.interface';

const useStyles = makeStyles(() => ({
  chatContainer: {
    width: '100%',
    height: 'calc(100vh - 60px)',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 0,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 60px)',
    gap: 16,
  },
}));

const CHAT_CONTAINER_ID = 'zendesk-chat-page-container';

export const ZendeskChatPage: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { subdomain } = useParams();
  const location = useLocation();
  const auth = useAuth();
  const { t } = useTranslation();
  const zendeskChatEnabled = usePostHogFeatureFlag('zendesk-ai-chat', false);
  const searchParams = new URLSearchParams(location.search);
  const chargerName = searchParams.get('charger');
  const sessionIdFromUrl = searchParams.get('sessionId');

  const { data: payment } = useFetchListOfPayMethodsQuery(undefined, {
    skip: !auth.isLoggedIn,
  });

  const { data: activeSessions } = useFetchActiveSessionsQuery(undefined, {
    skip: !auth.isLoggedIn,
  });

  const sessionId = sessionIdFromUrl ?? activeSessions?.[0]?.sessionId;

  const { data: chargerDetail, isLoading: isChargerLoading } = useFetchChargerDetailQuery(
    { chargerName: chargerName || '', isLoggedIn: auth.isLoggedIn },
    { skip: !chargerName },
  );

  const whiteLabellingId = chargerDetail?.whiteLabellingId ?? null;
  const resolvedKey = useZendeskChatConfig(whiteLabellingId);
  // When waiting for charger, don't use defaultChat - wait for correct key from contactInfo
  const isWaitingForCharger = chargerName && isChargerLoading;
  const zendeskKey = isWaitingForCharger ? null : resolvedKey;
  const { isReady, timedOut } = useZendeskChatReady();

  const conversationFields = useMemo((): ConversationField[] | undefined => {
    const fields: ConversationField[] = [];

    if (auth.user) {
      const userFirstName = (auth.user?.attributes?.given_name || '').trim();
      const userLastName = (auth.user?.attributes?.family_name || '').trim();
      const fullName = (userFirstName + ' ' + userLastName).trim();
      if (fullName) fields.push({ id: ZENDESK_FIELD_IDS.FULL_NAME, value: fullName });
      if (auth.user?.attributes?.email) {
        fields.push({ id: ZENDESK_FIELD_IDS.EMAIL, value: auth.user.attributes.email });
      }
      if (auth.user?.attributes?.phone_number) {
        fields.push({
          id: ZENDESK_FIELD_IDS.PHONE_NUMBER,
          value: auth.user.attributes.phone_number,
        });
      }
    }

    if (payment) {
      fields.push({
        id: ZENDESK_FIELD_IDS.DEFAULT_PAYMENT_METHOD_EXPIRED_STATUS,
        value: payment.hasValidPaymentMethod,
      });
    }

    if (chargerName) fields.push({ id: ZENDESK_FIELD_IDS.HARDWARE_CHARGER_ID, value: chargerName });

    if (sessionId) {
      fields.push({ id: ZENDESK_FIELD_IDS.SESSION_ID, value: sessionId });
    }

    if (chargerDetail) {
      fields.push({ id: ZENDESK_FIELD_IDS.CHARGER_UUID, value: chargerDetail.chargerId });
      fields.push({ id: ZENDESK_FIELD_IDS.CHARGER_NAME, value: chargerDetail.name });
      fields.push({
        id: ZENDESK_FIELD_IDS.CHARGER_UNDER_MAINTENANCE,
        value: chargerDetail.maintenanceFlag,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.CHARGER_UNIT_PRICE,
        value: `${chargerDetail.currentPrice?.unitPrice ?? ''}`,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.WHITE_LABELLING_NAME,
        value: chargerDetail.whiteLabellingName,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.WHITE_LABELLING_ID,
        value: chargerDetail.whiteLabellingId,
      });
      fields.push({ id: ZENDESK_FIELD_IDS.SITE_NAME, value: chargerDetail.location.name });
      fields.push({
        id: ZENDESK_FIELD_IDS.LOCATION_ID,
        value: chargerDetail.location.locationId,
      });
      if (chargerDetail.owner?.id) {
        fields.push({ id: ZENDESK_FIELD_IDS.COMPANY_UUID, value: chargerDetail.owner.id });
      }
      if (chargerDetail.userPriceAdjustment) {
        fields.push({
          id: ZENDESK_FIELD_IDS.USER_PRICE_ADJUSTMENT_TYPE,
          value: chargerDetail.userPriceAdjustment.type,
        });
        fields.push({
          id: ZENDESK_FIELD_IDS.USER_PRICE_ADJUSTMENT_VALUE,
          value: `${chargerDetail.userPriceAdjustment.value}`,
        });
      }
      fields.push({
        id: ZENDESK_FIELD_IDS.REQUIRE_PAYMENT_METHOD,
        value: chargerDetail.requirePaymentMethod,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.USER_HAS_ACCESS,
        value: chargerDetail.userHasAccess,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.CHARGER_IS_FREE,
        value: chargerDetail.currentPrice?.pricingType === PricingType.FREE,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.CHARGER_PRICE_TYPE,
        value: chargerDetail.currentPrice?.pricingType,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.CHARGER_MODEL_TEXT,
        value: chargerDetail.model,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.INITIAL_CHARGER_PORT_1_STATUS,
        value: chargerDetail.ports[0].status,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.CHARGER_PORT_1_CONNECTOR_TYPE,
        value: chargerDetail.ports[0].connectorTypes[0],
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.CHARGER_PORT_1_MAX_POWER_KILOWATTS,
        value: `${chargerDetail.ports[0].maxPowerKilowatts}`,
      });
      fields.push({
        id: ZENDESK_FIELD_IDS.CHARGER_PORT_1_CONNECTOR_LOCATION,
        value: chargerDetail.ports[0].connectorLocations[0],
      });

      if (chargerDetail.ports.length > 1) {
        fields.push({
          id: ZENDESK_FIELD_IDS.INITIAL_CHARGER_PORT_2_STATUS,
          value: chargerDetail.ports[1].status,
        });
        fields.push({
          id: ZENDESK_FIELD_IDS.CHARGER_PORT_2_CONNECTOR_TYPE,
          value: chargerDetail.ports[1].connectorTypes[0],
        });
        fields.push({
          id: ZENDESK_FIELD_IDS.CHARGER_PORT_2_MAX_POWER_KILOWATTS,
          value: `${chargerDetail.ports[1].maxPowerKilowatts}`,
        });
        fields.push({
          id: ZENDESK_FIELD_IDS.CHARGER_PORT_2_CONNECTOR_LOCATION,
          value: chargerDetail.ports[1].connectorLocations[0],
        });
      }

      if (chargerDetail.ports.length > 2) {
        fields.push({
          id: ZENDESK_FIELD_IDS.INITIAL_CHARGER_PORT_3_STATUS,
          value: chargerDetail.ports[2].status,
        });
        fields.push({
          id: ZENDESK_FIELD_IDS.CHARGER_PORT_3_CONNECTOR_TYPE,
          value: chargerDetail.ports[2].connectorTypes[0],
        });
        fields.push({
          id: ZENDESK_FIELD_IDS.CHARGER_PORT_3_MAX_POWER_KILOWATTS,
          value: chargerDetail.ports[2].maxPowerKilowatts,
        });
        fields.push({
          id: ZENDESK_FIELD_IDS.CHARGER_PORT_3_CONNECTOR_LOCATION,
          value: chargerDetail.ports[2].connectorLocations[0],
        });
      }
    }

    return fields.length > 0 ? fields : undefined;
  }, [auth.user, chargerName, chargerDetail, payment, sessionId]);

  useEffect(() => {
    if (isReady && zendeskKey) {
      renderZendeskChat(`#${CHAT_CONTAINER_ID}`, conversationFields);
    }
  }, [conversationFields, isReady, zendeskKey]);

  const handleBackClick = () => navigate(-1);

  const handleAccountClick = () => {
    navigate(
      `/app/${subdomain || window.domainName}/${ROUTES.ACCOUNT}${chargerName ? `?charger=${chargerName}` : ''}`,
    );
  };

  const isChatUnavailable = !zendeskChatEnabled || (!zendeskKey && !isWaitingForCharger);
  const showChat = zendeskChatEnabled && zendeskKey;

  return (
    <ViewWrapper>
      <BackButtonHeader
        onBackArrowClicked={handleBackClick}
        onIconClicked={handleAccountClick}
        icon={auth.isLoggedIn ? manageAccountIcon() : null}
      />
      {showChat && <ZendeskChat zendeskKey={zendeskKey} />}
      {(isWaitingForCharger || (showChat && !isReady)) && (
        <Box className={classes.loadingContainer}>
          {timedOut ? (
            <Typography color='textSecondary'>{t('chat_load_error')}</Typography>
          ) : (
            <CircularProgress />
          )}
        </Box>
      )}
      {isChatUnavailable && (
        <Box className={classes.loadingContainer}>
          <Typography color='textSecondary'>{t('chat_load_error')}</Typography>
        </Box>
      )}
      <div id={CHAT_CONTAINER_ID} className={classes.chatContainer} />
    </ViewWrapper>
  );
};

export default ZendeskChatPage;
